import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  formatDayTime,
  type OrderListItem,
  type OrderListResponse,
  ORDER_SOURCE_LABEL,
  RACK_TYPE_SHORT,
  STATUS_META,
} from '@reikraft/shared';
import { Chips, type Column, ErrorBox, Icon, Kpi, Kpis, Loader, Pager, SearchBox, StageBar, StatusBadge, Table, Tabs } from '@reikraft/ui';
import { api, qs } from '../api';
import { OrderCards, PageHead } from '../components/common';
import { downloadCsv, formatDay, formatMoney, partyKind } from '../format';
import { useDebounced, useIsMobile, useUrlState } from '../hooks';
import { useFlag, useTz } from '../session';

const STATUS_TABS = ['intake', 'diag', 'approve', 'repair', 'stand', 'ready', 'done'] as const;

export function OrdersPage() {
  const [state, set] = useUrlState({ tab: 'all', q: '', page: '1' });
  const [q, setQ] = useState(state.q);
  const dq = useDebounced(q);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const masters = useFlag('masters');
  const tz = useTz();

  useEffect(() => {
    if (dq !== state.q) set({ q: dq, page: '1' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const list = useQuery({
    queryKey: ['orders', state.tab, state.q, state.page],
    queryFn: () => api.get<OrderListResponse>(`/admin/orders${qs({ tab: state.tab, q: state.q, page: state.page, size: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const d = list.data;
  const counts = d?.counts ?? {};

  const tabs = [
    { id: 'all', label: 'Все', count: counts.all ?? 0 },
    ...(counts.requests ? [{ id: 'requests', label: 'Новые', count: counts.requests }] : []),
    ...STATUS_TABS.map((s) => ({ id: s, label: STATUS_META[s].name, count: counts[s] ?? 0 })),
    ...(counts.overdue ? [{ id: 'overdue', label: 'Просрочено', count: counts.overdue }] : []),
    ...(counts.closed ? [{ id: 'closed', label: 'Отмены и отказы', count: counts.closed }] : []),
  ];

  const columns: Column<OrderListItem>[] = [
    {
      key: 'n',
      title: '№ / Принят',
      width: '128px',
      render: (o) => (
        <span className="rk-td--stack">
          <b className="rk-mono" style={{ fontSize: 13 }}>
            {o.number}
          </b>
          <span className="rk-td__mono-sub">{formatDayTime(o.createdAt, tz)}</span>
        </span>
      ),
    },
    {
      key: 'client',
      title: 'Клиент',
      width: 'minmax(160px, 1.2fr)',
      render: (o) => (
        <span className="rk-td--stack">
          <span className="rk-row" style={{ ['--rk-gap' as string]: '8px', fontWeight: 600 }}>
            {o.party?.name ?? 'Пополнение фонда'}
            {o.party?.kind === 'organization' && <span className="rk-chip-b2b">B2B</span>}
          </span>
          <span className="rk-td__sub">
            {partyKind(o.party)} · {ORDER_SOURCE_LABEL[o.source]}
          </span>
        </span>
      ),
    },
    {
      key: 'car',
      title: 'Авто · рейка',
      width: 'minmax(160px, 1fr)',
      render: (o) => (
        <span className="rk-td--stack">
          <span>{o.car ?? '—'}</span>
          <span className="rk-td__mono-sub">
            {o.rackType ? RACK_TYPE_SHORT[o.rackType] : '—'}
            {o.plate ? ` · ${o.plate}` : ''}
          </span>
        </span>
      ),
    },
    { key: 'status', title: 'Статус', width: '150px', render: (o) => <StatusBadge status={o.status} kind={o.kind} /> },
    { key: 'stages', title: 'Этапы', width: '104px', render: (o) => <StageBar kind={o.kind} status={o.status} /> },
    ...(masters
      ? [
          {
            key: 'master',
            title: 'Мастер',
            width: '120px',
            render: (o: OrderListItem) => <span className="rk-text-2">{o.masterName ?? '—'}</span>,
          },
        ]
      : []),
    {
      key: 'due',
      title: 'Срок',
      width: '90px',
      render: (o) => (
        <span className="rk-td--stack rk-mono" style={{ fontSize: 12, color: o.overdue ? 'var(--danger-text)' : 'var(--text-2)' }}>
          {o.dueDate ? formatDay(o.dueDate) : '—'}
          {o.overdue && <span style={{ fontSize: 10, fontWeight: 700 }}>ПРОСРОЧЕН</span>}
        </span>
      ),
    },
    {
      key: 'sum',
      title: 'Сумма',
      width: '100px',
      align: 'right',
      render: (o) => (
        <b className="rk-mono" title={o.totalKop && o.paidKop >= o.totalKop ? 'Оплачен' : undefined}>
          {formatMoney(o.totalKop)}
        </b>
      ),
    },
  ];

  const exportCsv = () => {
    if (!d) return;
    downloadCsv(`заказы-${new Date().toISOString().slice(0, 10)}.csv`, [
      ['Номер', 'Принят', 'Клиент', 'Тип клиента', 'Авто', 'Госномер', 'Рейка', 'Статус', 'Срок', 'Сумма, ₽', 'Оплачено, ₽'],
      ...d.items.map((o) => [
        o.number,
        formatDayTime(o.createdAt, tz),
        o.party?.name ?? '',
        partyKind(o.party),
        o.car ?? '',
        o.plate ?? '',
        o.rackType ? RACK_TYPE_SHORT[o.rackType] : '',
        STATUS_META[o.status].name,
        o.dueDate ?? '',
        o.totalKop !== null ? o.totalKop / 100 : '',
        o.paidKop / 100,
      ]),
    ]);
  };

  return (
    <>
      <PageHead
        title="Заказы"
        actions={
          <>
            <button type="button" className="rk-btn adm-hide-sm" onClick={exportCsv} disabled={!d?.items.length}>
              Экспорт
            </button>
            <Link to="/orders/new" className="rk-btn rk-btn--primary">
              <Icon name="plus" size={16} /> Новый заказ
            </Link>
          </>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder="№ заказа, клиент, авто, госномер, телефон" label="Поиск заказа" />
      </PageHead>

      {d && (
        <Kpis min={isMobile ? 130 : 160}>
          <Kpi label="В работе" value={d.kpi.inWork} />
          <Kpi label="Ждут согласования" value={d.kpi.approve} dot={STATUS_META.approve.color} />
          <Kpi label="Готовы к выдаче" value={d.kpi.ready} dot={STATUS_META.ready.color} />
          <Kpi label="Просрочено" value={d.kpi.overdue} dot="var(--danger)" tone={d.kpi.overdue ? 'danger' : undefined} />
          {!isMobile && <Kpi label={`Поступления · ${d.kpi.monthName}`} value={formatMoney(d.kpi.revenueMonthKop)} />}
        </Kpis>
      )}

      {isMobile ? (
        <Chips items={tabs} value={state.tab} onChange={(tab) => set({ tab, page: '1' })} label="Статус заказа" />
      ) : (
        <Tabs items={tabs} value={state.tab} onChange={(tab) => set({ tab, page: '1' })} label="Статус заказа" />
      )}

      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => list.refetch()} />
      ) : isMobile ? (
        <OrderCards items={d!.items} showMaster={masters} />
      ) : (
        <Table
          label="Заказы"
          columns={columns}
          rows={d!.items}
          rowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/orders/${o.id}`)}
          minWidth={1060}
          empty="Ничего не найдено. Проверьте номер заказа или сбросьте фильтр."
        />
      )}
      {d && <Pager page={d.page} pages={d.pages} total={d.total} shown={d.items.length} onPage={(p) => set({ page: String(p) })} />}
    </>
  );
}
